import axios from "axios";
import { getLang, tr, withLang } from "./i18n";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("billett_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Språkvalg følger med til backend (brukes til e-poster på riktig språk)
  config.headers["X-Lang"] = getLang();
  return config;
});

export const CATEGORIES = ["Konsert", "Teater", "Sport", "Festival", "Foredrag", "Annet"];

// Lyst, varmt sommerfestival-bilde (dagslys, vimpler, blå himmel) – brukes når arrangør ikke har lastet opp bilde
export const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1472653431158-6364773b2a56?auto=format&fit=crop&w=1200&q=80";

export function imageSrc(url) {
  if (!url) return FALLBACK_IMAGE;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

// Delingslenke via /api/share (Open Graph for sosiale medier) – med språkvalget til den som deler,
// så mottakeren får arrangementssiden på samme målform.
export function eventShareUrl(eventId) {
  return withLang(`${process.env.REACT_APP_BACKEND_URL}/api/share/${eventId}`);
}

export function openFacebookShare(url) {
  window.open(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    "_blank",
    "noopener,noreferrer,width=626,height=436"
  );
}

// Nynorsk ukedager: Intl mangler «nn» i flere nettlesere (faller til engelsk), så vi formaterer på nb-NO og bytter navn.
const NN_WEEKDAYS = [
  [/\bmandag\b/gi, "måndag"], [/\btirsdag\b/gi, "tysdag"], [/\blørdag\b/gi, "laurdag"], [/\bsøndag\b/gi, "sundag"],
  [/\bman\.\b/gi, "mån."], [/\btir\.\b/gi, "tys."], [/\blør\.\b/gi, "lau."], [/\bsøn\.\b/gi, "sun."],
  [/\bman\b(?!\.)/g, "mån"], [/\btir\b(?!\.)/g, "tys"], [/\blør\b(?!\.)/g, "lau"], [/\bsøn\b(?!\.)/g, "sun"],
];

function nynorskify(s) {
  if (getLang() !== "nn") return s;
  return NN_WEEKDAYS.reduce((acc, [re, to]) => acc.replace(re, to), s);
}

export function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return nynorskify(new Intl.DateTimeFormat("nb-NO", {
      weekday: "short",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d));
  } catch {
    return iso;
  }
}

export function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("nb-NO", { hour: "2-digit", minute: "2-digit" }).format(d);
  } catch {
    return "";
  }
}

// Generell datoformatering med valgfrie Intl-opsjoner – alltid norsk, nynorsk ukedager når valgt
export function formatLocal(iso, options) {
  if (!iso) return "";
  try {
    return nynorskify(new Intl.DateTimeFormat("nb-NO", options).format(new Date(iso)));
  } catch {
    return "";
  }
}

export function formatDateTime(iso) {
  if (!iso) return "";
  const date = formatDate(iso);
  const time = formatTime(iso);
  return time ? `${date} ${tr("kl.")} ${time}` : date;
}

export function priceLabel(event) {
  const min = event?.min_price ?? 0;
  const max = event?.max_price ?? 0;
  if (max === 0) return tr("Gratis");
  if (min === max) return tr("{min} kr", { min });
  if (min === 0) return tr("Gratis – {max} kr", { max });
  return tr("Fra {min} kr", { min });
}

export function errorMessage(err, fallback = tr("Noe gikk galt. Prøv igjen.")) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail) return tr(detail);
  if (detail && typeof detail === "object" && !Array.isArray(detail) && detail.message) return tr(detail.message);
  if (Array.isArray(detail) && detail.length > 0) {
    return detail[0]?.msg || fallback;
  }
  return fallback;
}

// Teknisk årsak (f.eks. fra AI-tjenesten) når backend sender {message, technical}
export function errorTechnical(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail) && detail.technical) return String(detail.technical);
  return "";
}

// «Så enkelt er det å komme i gang med Right in» – rettleiing for arrangørar (PDF, bokmål/nynorsk)
export function organizerGuideUrl(lang) {
  const l = lang === "nn" ? "nn" : "nb";
  return `/docs/rightin-kom-i-gang-for-arrangorar-${l}.pdf`;
}
